import React from "react";
import { ImportantDocs, RequireDocs } from "../../types/serviceDetail";

interface Props {
  sectionTitle: string | null;
  line: string | null;
  sectionTitle2: string | null;
  line2: string | null;
  isPage?: boolean;
  requireDocs?: RequireDocs;
  importantNotes?: ImportantDocs;
}

const ServiceInfo: React.FC<Props> = ({
  sectionTitle,
  line,
  sectionTitle2,
  line2,
  isPage = false,
  requireDocs,
  importantNotes,
}) => {
  const [isVisible, setIsVisible] = React.useState<{ [key: string]: boolean }>({
    svg1: false,
    svg2: false,
    svg3: false,
  });

  const svgRefs = React.useRef<(HTMLImageElement | null)[]>([]);

  React.useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const key = entry.target.getAttribute("data-key") as string;
        if (key && isVisible.hasOwnProperty(key)) {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [key]: true }));
          }
        }
      });
    }, { threshold: 0.5 });

    svgRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      svgRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []); // Empty dependency array to ensure it's set up once

  const renderTitle = (title: string | null) => {
    if (!title) return null;
    const TitleTag = isPage ? "h2" : "p";
    return (
      <TitleTag
        className={`text-center font-16 text-black ff-semibold ${isPage ? 'mb-4' : ''}`}
        dangerouslySetInnerHTML={{ __html: title }}
      />
    );
  };

  const renderLine = (line: string | null) => {
    return line ? (
      <p className="font-12 text-grey ff-normal text-center" dangerouslySetInnerHTML={{ __html: line }} />
    ) : null;
  };

  const renderImageColumn = (index: number, src: string, alt: string) => (
    <div className={index === 1 ? "" : "col-4"}>  {/* Check if the index is 1 */}
      <img
        ref={(el) => (svgRefs.current[index] = el)}
        data-key={`svg${index + 1}`} // Data attribute for the key
        src={`${src}?t=${Date.now()}`}
        className={`animatedd-svg d-block mx-auto img-fluid ${isVisible[`svg${index + 1}`] ? "visible" : "hidden"}`}
        alt={alt}
        style={{ width: "100%", height: "auto", objectFit: "contain" }} // Ensure consistent size and aspect ratio
      />
    </div>
  );

  const renderDocs = (docs: Array<any>, type: string) => docs.length > 0 && (
    <div className={`p-3 layout-border rounded mt-3 ${type === 'important' ? 'bg-4 my-3' : ''}`}>
      {type === 'important' && <h3 className="font-14 ff-semibold text-noti mb-2">Important Note:</h3>}
      <ol className={`font-12 ${type === 'important' ? 'text-noti' : ''} px-3`}>
        {docs.map((doc, index) => (
          <li
            key={`${type}-doc-${index}`}
            className={`pb-${type === 'important' ? 3 : 1} ${type === 'important' ? 'ff-medium' : 'pb-md-3'}`}
            dangerouslySetInnerHTML={{ __html: doc.TagLine ?? doc ?? "" }}
          />
        ))}
      </ol>
    </div>
  );

  const renderFeaturedDocs = () => (
    <div className="d-flex align-items-center justify-content-center gap-2 pb-3 mb-3">
      {requireDocs?.featureDocs?.map((option, index) => (
        <div
          key={`featured-doc-${index}`}
          className="d-flex align-items-center justify-content-start gap-3 p-2 px-md-3 px-1 bg-2 rounded-pill"
        >
          {option.icon && (
            <img
              src={option.icon || "/path-to-default-image.png"}
              alt={option.shortName || "Default name"}
            />
          )}
          <p
            className="mb-0 font-12 font-sm-10 text-grey ff-semibold"
            dangerouslySetInnerHTML={{ __html: option.shortName ?? "" }}
          />
        </div>
      ))}
    </div>
  );

  return (
    <div className="applicable-main-wrap py-4">
      {renderTitle(sectionTitle)}
      {renderLine(line)}
      {requireDocs && renderFeaturedDocs()}
      {renderTitle(sectionTitle2)}
      {renderLine(line2)}
      
      <div className="row">
        {renderImageColumn(0, "https://goodhand.b-cdn.net/Assets/cart/animate1.svg", "Animated 1")}
        <div className="col-4">
          <p className="font-12 mb-0 font-sm-8 text-black ff-bold text-center">
            Speed That Saves You Time
          </p>
          {renderImageColumn(1, "https://goodhand.b-cdn.net/Assets/cart/gauge-main.svg", "Gauge Animation")}
          <p className="font-10 mb-0 text-grey text-center ff-medium">
            Why It's Easy to Apply
          </p>
        </div>
        {renderImageColumn(2, "https://goodhand.b-cdn.net/Assets/cart/animate-3.svg", "Animated 3")}
      </div>
      
      {requireDocs?.requiredDocs && renderDocs(requireDocs.requiredDocs, 'regular')}
      {importantNotes?.insideDescription && renderDocs(importantNotes.insideDescription.split("---"), 'important')}
    </div>
  );
};

export default ServiceInfo;
